import SEO from "../components/SEO";
import Layout from "../components/Layout";
import CarValueCalculator from "../components/CarValueCalculator";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, MapPin, TrendingUp, Info } from "lucide-react";
import { STATE_CALC_CONTENT } from "../data/state_calculator_content";

// Maps for the 4 prioritized state landings (kept for backwards-compat —
// state-specific content is now driven entirely by STATE_CALC_CONTENT).
const STATE_CONFIG = {
  california: { name: "California", code: "CA", billOfSalePath: "/california-car-bill-of-sale" },
  texas: { name: "Texas", code: "TX", billOfSalePath: "/texas-car-bill-of-sale" },
  florida: { name: "Florida", code: "FL", billOfSalePath: "/florida-car-bill-of-sale" },
  "new-york": { name: "New York", code: "NY", billOfSalePath: "/new-york-car-bill-of-sale" },
};

const FAQS = [
  {
    q: "What is private sale value?",
    a: "Private sale value is the price you can realistically expect from another individual buying your car directly from you — without going through a dealer. It sits below dealer retail (because the buyer takes on more risk and no warranty) but above what a dealer would pay you on trade-in.",
  },
  {
    q: "Is this an official appraisal?",
    a: "No. This is an informational price range based on general market data and the details you provided. It is not an appraisal, not a valuation, and not a guarantee of what your vehicle will sell for. For a binding valuation, contact a licensed appraiser, your insurance company, or your bank.",
  },
  {
    q: "Why is private sale value different from trade-in value?",
    a: "Dealers pay less on trade-in because they need to recondition the car, store it, advertise it and resell it for a profit — plus they assume the warranty risk. Private buyers skip those middle steps, so you can charge closer to the dealer's retail price.",
  },
  {
    q: "What affects a car's private sale price?",
    a: "Year, make, model, trim, mileage and condition are the big drivers (about 75% of the price). Title status (clean vs salvage), accident history, your ZIP / state and local demand can swing the final number by 10–30%.",
  },
  {
    q: "Should I sell privately or trade in my car?",
    a: "If you have time (typically 2–6 weeks), selling privately often nets you $1,000–$3,000 more. If you need the money now or you're rolling into another car at the same dealer, trade-in is simpler. Use the calculator to see your specific difference.",
  },
  {
    q: "Do I need a bill of sale after agreeing on a price?",
    a: "Yes. Every US state expects a written bill of sale documenting the year, make, model, VIN, sale price, odometer reading, and signatures of both parties. It protects both seller and buyer and is required for the buyer to title and register the vehicle.",
  },
  {
    q: "Does mileage affect private sale value?",
    a: "Yes — significantly. A car with fewer than typical miles for its age can sell for 5–10% more; a car with more than typical miles drops in the same proportion. Our calculator applies a mileage adjustment based on what's typical for the model year.",
  },
  {
    q: "Does a salvage title affect the estimate?",
    a: "Yes, heavily. A salvage title typically cuts a car's value by 50–60% versus a clean title; a rebuilt title cuts it by roughly 35%. The calculator applies that adjustment automatically when you set the title status.",
  },
];

const TIPS = [
  "Detail the car: a $200 professional detail can add $500–$1,000 to the perceived value.",
  "Get a recent oil change and gather all maintenance records — buyers will pay more for documented service history.",
  "Take 10–15 well-lit photos: exterior from 4 angles, dashboard, odometer, seats, engine bay, tires.",
  "Price it 5–10% above your target so you have room to negotiate.",
  "Be honest about defects in the listing — surprises kill deals at the meetup.",
  "Always meet the buyer in a safe, public, well-lit location during the day.",
  "Accept cash, cashier's check (verified at issuing bank) or escrow — never personal checks for full amount.",
  "Sign a Bill of Sale at the meetup. Both parties keep a signed copy.",
];

export default function Calculator({ stateSlug = null }) {
  const stateCfg = stateSlug ? STATE_CONFIG[stateSlug] : null;
  const isStatePage = !!stateCfg;
  const stateName = stateCfg?.name || null;
  const stateCode = stateCfg?.code || null;
  const stateContent = stateSlug ? STATE_CALC_CONTENT[stateSlug] : null;

  // FAQs shown on a state page = state-specific first, then 3 universal ones
  // (so the question set is genuinely different per landing — important for
  // FAQPage schema uniqueness in Google's eyes).
  const pageFaqs = stateContent
    ? [...stateContent.extraFaqs, ...FAQS.slice(0, 3)]
    : FAQS;

  const path = isStatePage
    ? `/private-sale-car-value-calculator/${stateSlug}`
    : "/private-sale-car-value-calculator";
  const seoTitle = isStatePage
    ? `${stateName} Car Value Calculator | Private Sale Estimate`
    : "Private Sale Car Value Calculator | Free Estimate";
  const seoDescription = stateContent
    ? `${stateContent.marketOverview.headline}. Free ${stateName} car value calculator — estimate private sale range and compare with trade-in.`.substring(0, 158)
    : "Free car value calculator. Estimate what your car could sell for in a private sale based on mileage, condition, title and location. Compare with trade-in.";

  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: pageFaqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
  const webAppJsonLd = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    name: isStatePage ? `${stateName} Private Sale Car Value Calculator` : "Private Sale Car Value Calculator",
    url: `https://www.carsaledoc.com${path}`,
    applicationCategory: "FinanceApplication",
    operatingSystem: "Any",
    offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
    description: seoDescription,
  };
  const breadcrumbLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://www.carsaledoc.com/" },
      { "@type": "ListItem", position: 2, name: "Car value calculator", item: "https://www.carsaledoc.com/private-sale-car-value-calculator" },
      ...(isStatePage ? [{ "@type": "ListItem", position: 3, name: stateName, item: `https://www.carsaledoc.com${path}` }] : []),
    ],
  };

  return (
    <Layout>
      <SEO title={seoTitle} description={seoDescription} path={path} jsonLd={[webAppJsonLd, faqJsonLd, breadcrumbLd]} />

      <CarValueCalculator defaultState={stateCode} stateName={stateName} />

      {/* CALCULATE BY STATE — only on the main /private-sale-car-value-calculator hub */}
      {!isStatePage && (
        <section className="max-w-6xl mx-auto px-6 py-12">
          <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight mb-2">
            Calculate by state
          </h2>
          <p className="text-[15px] text-[var(--csd-text-secondary)] mb-6 max-w-3xl">
            Prices, demand and DMV rules vary by state. Each state page below adapts the calculator and adds local market context (tax rules, seasonal demand, top-selling models).
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { slug: "california", name: "California", img: "https://images.unsplash.com/photo-1663328319236-86eee3352311?crop=entropy&cs=srgb&fm=jpg&w=800&q=80", tag: "Smog · REG 135 · EV premium" },
              { slug: "texas", name: "Texas", img: "https://images.unsplash.com/photo-1592190057402-2bf1ee02118d?crop=entropy&cs=srgb&fm=jpg&w=800&q=80", tag: "Truck market · SPV · Rust-free" },
              { slug: "florida", name: "Florida", img: "https://images.unsplash.com/photo-1560427791-bcf0b027ff44?crop=entropy&cs=srgb&fm=jpg&w=800&q=80", tag: "Snowbird season · HSMV 82050" },
              { slug: "new-york", name: "New York", img: "https://images.unsplash.com/photo-1514565131-fce0801e5785?crop=entropy&cs=srgb&fm=jpg&w=800&q=80", tag: "MV-912 · NYC vs upstate" },
            ].map((s) => (
              <Link
                key={s.slug}
                to={`/private-sale-car-value-calculator/${s.slug}`}
                data-testid={`calc-state-card-${s.slug}`}
                className="group block rounded-2xl overflow-hidden border border-[var(--csd-border)] bg-white hover:-translate-y-0.5 hover:shadow-lg transition-all"
              >
                <div className="relative aspect-[4/3] overflow-hidden">
                  <img src={s.img} alt={`${s.name} private car sale`} className="w-full h-full object-cover group-hover:scale-105 transition-transform" loading="lazy" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/15 to-transparent" />
                  <div className="absolute bottom-3 left-4 right-4 text-white">
                    <div className="text-xs uppercase tracking-widest font-semibold opacity-90">{s.tag}</div>
                    <div className="font-heading text-xl font-extrabold mt-0.5">{s.name}</div>
                  </div>
                </div>
                <div className="p-3 flex items-center justify-between text-sm">
                  <span className="font-semibold text-[var(--csd-text-primary)]">Calculate for {s.name}</span>
                  <ArrowRight size={14} className="text-[var(--csd-primary)] group-hover:translate-x-1 transition-transform" />
                </div>
              </Link>
            ))}
          </div>
          <p className="text-xs text-[var(--csd-text-secondary)] mt-4">
            Not in these states? Use the main calculator above — it works for every US state and applies a state-level demand adjustment when you enter your ZIP.
          </p>
        </section>
      )}

      {/* STATE-SPECIFIC MARKET CONTENT (only on /[state] sub-routes) */}
      {stateContent && (
        <>
          <section className="max-w-5xl mx-auto px-6 py-12">
            <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[var(--csd-primary)] font-semibold mb-3">
              <TrendingUp size={14} /> {stateContent.name} used car market
            </div>
            <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight mb-3">
              {stateContent.marketOverview.headline}
            </h2>
            {stateContent.marketOverview.paragraphs.map((p, i) => (
              <p key={i} className="text-[15px] text-[var(--csd-text-secondary)] leading-relaxed mb-3 max-w-3xl">
                {p}
              </p>
            ))}
          </section>

          <section className="max-w-5xl mx-auto px-6 pb-12">
            <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight mb-3">
              Top-selling used vehicles in {stateContent.name}
            </h2>
            <p className="text-[15px] text-[var(--csd-text-secondary)] max-w-3xl mb-5">
              Based on public 2025–2026 US used car market data. These models tend to sell faster and hold value better in {stateContent.name}.
            </p>
            <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
              {stateContent.topModels.map((m) => (
                <li key={m} className="flex items-start gap-2 text-[var(--csd-text-primary)]">
                  <CheckCircle2 size={14} className="text-[var(--csd-primary)] mt-1 shrink-0" />
                  <span className="leading-relaxed">{m}</span>
                </li>
              ))}
            </ul>
          </section>

          <section className="max-w-5xl mx-auto px-6 pb-12">
            <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[var(--csd-primary)] font-semibold mb-2">
              <MapPin size={14} /> {stateContent.name}-specific
            </div>
            <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight mb-5">
              {stateContent.quirks.title}
            </h2>
            <div className="space-y-4">
              {stateContent.quirks.items.map((q) => (
                <div key={q.h} className="csd-card p-5 sm:p-6">
                  <h3 className="font-heading font-bold text-base text-[var(--csd-text-primary)] mb-2 flex items-start gap-2">
                    <Info size={16} className="text-[var(--csd-primary)] mt-0.5 shrink-0" />
                    {q.h}
                  </h3>
                  <p className="text-sm text-[var(--csd-text-secondary)] leading-relaxed">{q.p}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="max-w-5xl mx-auto px-6 pb-12">
            <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight mb-4">
              Tips to maximize your private sale price in {stateContent.name}
            </h2>
            <ol className="space-y-3">
              {stateContent.tips.map((t, i) => (
                <li key={t} className="flex items-start gap-3 text-[15px]">
                  <span className="shrink-0 w-7 h-7 rounded-full bg-[var(--csd-primary-light)] text-[var(--csd-primary)] grid place-items-center text-xs font-bold">{i + 1}</span>
                  <span className="text-[var(--csd-text-primary)] leading-relaxed">{t}</span>
                </li>
              ))}
            </ol>
          </section>

          {/* OTHER STATE CALCULATORS */}
          <section className="max-w-6xl mx-auto px-6 pb-12">
            <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight mb-4">Selling in another state? Try these calculators</h2>
            <div className="grid sm:grid-cols-3 gap-4">
              {[
                { slug: "california", name: "California", img: "https://images.unsplash.com/photo-1663328319236-86eee3352311?crop=entropy&cs=srgb&fm=jpg&w=800&q=80", tag: "Smog · REG 135 · EV premium" },
                { slug: "texas", name: "Texas", img: "https://images.unsplash.com/photo-1592190057402-2bf1ee02118d?crop=entropy&cs=srgb&fm=jpg&w=800&q=80", tag: "Truck market · SPV · Rust-free" },
                { slug: "florida", name: "Florida", img: "https://images.unsplash.com/photo-1560427791-bcf0b027ff44?crop=entropy&cs=srgb&fm=jpg&w=800&q=80", tag: "Snowbird season · HSMV 82050" },
                { slug: "new-york", name: "New York", img: "https://images.unsplash.com/photo-1514565131-fce0801e5785?crop=entropy&cs=srgb&fm=jpg&w=800&q=80", tag: "MV-912 · NYC vs upstate" },
              ].filter((s) => s.slug !== stateSlug).map((s) => (
                <Link
                  key={s.slug}
                  to={`/private-sale-car-value-calculator/${s.slug}`}
                  data-testid={`state-other-${s.slug}`}
                  className="group block rounded-2xl overflow-hidden border border-[var(--csd-border)] bg-white hover:-translate-y-0.5 hover:shadow-lg transition-all"
                >
                  <div className="relative aspect-[16/10] overflow-hidden">
                    <img src={s.img} alt={`${s.name} private car sale`} className="w-full h-full object-cover group-hover:scale-105 transition-transform" loading="lazy" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/15 to-transparent" />
                    <div className="absolute bottom-3 left-4 right-4 text-white">
                      <div className="text-xs uppercase tracking-widest font-semibold opacity-90">{s.tag}</div>
                      <div className="font-heading text-lg font-extrabold mt-0.5">{s.name}</div>
                    </div>
                  </div>
                  <div className="p-3 flex items-center justify-between text-sm">
                    <span className="font-semibold text-[var(--csd-text-primary)]">{s.name} calculator</span>
                    <ArrowRight size={14} className="text-[var(--csd-primary)] group-hover:translate-x-1 transition-transform" />
                  </div>
                </Link>
              ))}
            </div>
          </section>
        </>
      )}

      {/* HOW IT WORKS */}
      <section className="max-w-5xl mx-auto px-6 py-12">
        <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight mb-2">
          How much is your car worth in a private sale?
        </h2>
        <p className="text-[15px] text-[var(--csd-text-secondary)] max-w-3xl leading-relaxed">
          Private sale value sits between dealer retail (highest, what a dealer would ask) and trade-in (lowest, what a dealer would pay you).
          Private buyers skip the middle steps a dealer adds, so you can usually keep <strong>{stateName ? `more of your ${stateName} car's value` : "more of your car's value"}</strong> by selling directly — typically <strong>$1,000–$3,000 more</strong> than trading it in.
        </p>
      </section>

      {/* PRIVATE VS TRADE-IN */}
      <section className="max-w-5xl mx-auto px-6 pb-12">
        <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight mb-4">Private sale value vs trade-in value</h2>
        <div className="grid md:grid-cols-3 gap-4">
          <div className="csd-card p-5">
            <div className="text-xs uppercase tracking-widest text-[var(--csd-text-secondary)] font-semibold mb-1">Dealer retail</div>
            <h3 className="font-heading text-lg font-bold mb-2">Highest price</h3>
            <p className="text-sm text-[var(--csd-text-secondary)]">What a dealer asks on the lot — includes reconditioning cost, dealer overhead, financing desk markup and a profit margin.</p>
          </div>
          <div className="csd-card p-5 bg-[var(--csd-primary-light)]/40 border-[var(--csd-primary-light)]">
            <div className="text-xs uppercase tracking-widest text-[var(--csd-primary)] font-semibold mb-1">Private sale</div>
            <h3 className="font-heading text-lg font-bold mb-2">Middle ground — best for sellers</h3>
            <p className="text-sm text-[var(--csd-text-primary)]">What individual buyers pay you directly. Below dealer retail (they take more risk, no warranty) but well above trade-in.</p>
          </div>
          <div className="csd-card p-5">
            <div className="text-xs uppercase tracking-widest text-[var(--csd-text-secondary)] font-semibold mb-1">Trade-in</div>
            <h3 className="font-heading text-lg font-bold mb-2">Lowest — but instant</h3>
            <p className="text-sm text-[var(--csd-text-secondary)]">What a dealer pays you to take the car. Convenient if rolling into a new purchase, but typically 25–30% below dealer retail.</p>
          </div>
        </div>
      </section>

      {/* HOW WE CALCULATE */}
      <section className="max-w-5xl mx-auto px-6 pb-12">
        <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight mb-3">How we calculate your estimate</h2>
        <p className="text-[15px] text-[var(--csd-text-secondary)] max-w-3xl leading-relaxed mb-4">
          We start from a current-year retail price for your year, make and model. We then apply a typical depreciation curve to roll back to your model year, and add adjustments for:
        </p>
        <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm text-[var(--csd-text-primary)]">
          {["Mileage vs typical for the model year", "Overall condition (poor → excellent)", "Title status (clean / rebuilt / salvage)", "Accident history (none / minor / major)", "State and ZIP-level demand", "Optional trim level if available"].map((bullet) => (
            <li key={bullet} className="flex items-start gap-2"><CheckCircle2 size={14} className="text-[var(--csd-primary)] mt-1 shrink-0" />{bullet}</li>
          ))}
        </ul>
      </section>

      {/* TIPS */}
      <section className="max-w-5xl mx-auto px-6 pb-12">
        <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight mb-3">Tips to sell your car privately for more</h2>
        <ol className="space-y-3">
          {TIPS.map((tip, i) => (
            <li key={tip} className="flex items-start gap-3 text-[15px]">
              <span className="shrink-0 w-7 h-7 rounded-full bg-[var(--csd-primary-light)] text-[var(--csd-primary)] grid place-items-center text-xs font-bold">{i + 1}</span>
              <span className="text-[var(--csd-text-primary)] leading-relaxed">{tip}</span>
            </li>
          ))}
        </ol>
      </section>

      {/* AFTER YOU AGREE */}
      <section className="max-w-5xl mx-auto px-6 pb-12">
        <div className="csd-card p-6 sm:p-8 bg-gradient-to-br from-[var(--csd-primary-light)]/40 to-white">
          <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight mb-3">What to do after you agree on a price</h2>
          <p className="text-[15px] text-[var(--csd-text-secondary)] max-w-3xl leading-relaxed mb-4">
            A handshake is not enough. Document the transaction with a Bill of Sale — every US state expects one to title and register the vehicle.
            CarSaleDoc generates a {stateName ? `${stateName} ` : ""}DMV-friendly bill of sale, captures both digital signatures, and emails the signed PDF to both parties.
          </p>
          <Link
            to={isStatePage ? `${stateCfg.billOfSalePath}` : "/car-bill-of-sale"}
            data-testid="calc-final-cta-billofsale"
            className="csd-btn-primary px-5 py-3 rounded-xl text-sm font-semibold inline-flex items-center gap-2"
          >
            {stateName ? `Create a ${stateName} Bill of Sale` : "Create Your Bill of Sale"} <ArrowRight size={14} />
          </Link>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-5xl mx-auto px-6 pb-16">
        <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight mb-5">
          {stateContent ? `${stateContent.name}-specific FAQs` : "Frequently asked questions"}
        </h2>
        <div className="space-y-3">
          {pageFaqs.map((f) => (
            <details key={f.q} className="csd-card p-5 group">
              <summary className="cursor-pointer font-semibold text-[var(--csd-text-primary)] flex items-center justify-between gap-2">
                {f.q}
                <span className="text-[var(--csd-primary)] group-open:rotate-45 transition-transform">＋</span>
              </summary>
              <p className="mt-3 text-sm text-[var(--csd-text-secondary)] leading-relaxed">{f.a}</p>
            </details>
          ))}
        </div>
      </section>
    </Layout>
  );
}
